"use client";

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@repo/ui";
import Image from "next/image";
import Link from "next/link";

import type { WhatsNewNavItem } from "@/lib/whats-new";

import { headerGroups, type NavLink, type NavSection } from "./nav-links";

// Panels size to their content: one fixed-width column per section, plus the
// What's New card where it applies. A single shared width across four groups
// would leave the two-section panels mostly empty: the Radix viewport
// animates between sizes, so a per-group width is the better trade.
const SECTION_COLUMN = "300px";
const WHATS_NEW_COLUMN = "280px";

// The group whose panel carries the featured What's New card.
const WHATS_NEW_GROUP = "Resources";

const DARK_TRIGGER_CLASS =
  "bg-transparent text-white hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white data-[state=open]:bg-white/10 data-[state=open]:text-white data-[state=open]:hover:bg-white/10 data-[state=open]:focus:bg-white/10";

function PanelLink({ link }: { link: NavLink }) {
  return (
    <NavigationMenuLink asChild>
      <Link href={link.href}>
        <div className="text-label-14 font-medium">{link.name}</div>
        <p className="text-foreground/60 text-copy-14">{link.description}</p>
      </Link>
    </NavigationMenuLink>
  );
}

function PanelLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-foreground/50 text-label-12-mono px-2 pt-1 pb-2 tracking-widest uppercase">
      {children}
    </div>
  );
}

function PanelSection({ section }: { section: NavSection }) {
  return (
    <div className="flex flex-col gap-1 px-4 first:pl-1 last:pr-1">
      <PanelLabel>{section.label}</PanelLabel>
      {section.links.map((link) => (
        <PanelLink key={link.href} link={link} />
      ))}
    </div>
  );
}

function WhatsNewCard({ item }: { item: WhatsNewNavItem }) {
  return (
    <div className="flex flex-col px-4 last:pr-1">
      <PanelLabel>What&apos;s new</PanelLabel>
      <NavigationMenuLink asChild>
        <Link href={`/whats-new/${item.slug}`}>
          {item.cover && (
            <Image
              src={item.cover}
              alt={item.title}
              width={480}
              height={270}
              className="border-border aspect-video w-full rounded-md border object-cover"
            />
          )}
          <div className="text-foreground/60 flex items-center gap-2 pt-1 text-xs">
            <span className="border-border rounded-full border px-2 py-0.5 font-medium">
              {item.category}
            </span>
            {item.publishedAgo}
          </div>
          <div className="text-label-14 font-medium">{item.title}</div>
          <p className="text-foreground/60 text-copy-14 line-clamp-2">
            {item.description}
          </p>
        </Link>
      </NavigationMenuLink>
    </div>
  );
}

export function MarketingNavMenu({
  variant = "light",
  whatsNew,
}: {
  variant?: "light" | "dark";
  whatsNew?: WhatsNewNavItem | null;
}) {
  const triggerClass = variant === "dark" ? DARK_TRIGGER_CLASS : undefined;

  return (
    <NavigationMenu className="max-lg:hidden">
      <NavigationMenuList>
        {headerGroups.map((group) => {
          const showWhatsNew =
            Boolean(whatsNew) && group.label === WHATS_NEW_GROUP;
          return (
            <NavigationMenuItem key={group.label}>
              <NavigationMenuTrigger className={triggerClass}>
                {group.label}
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <div
                  className="grid p-3"
                  style={{
                    gridTemplateColumns: [
                      `repeat(${group.sections.length}, ${SECTION_COLUMN})`,
                      showWhatsNew ? WHATS_NEW_COLUMN : "",
                    ]
                      .join(" ")
                      .trim(),
                  }}
                >
                  {group.sections.map((section) => (
                    <PanelSection key={section.label} section={section} />
                  ))}
                  {whatsNew && showWhatsNew && <WhatsNewCard item={whatsNew} />}
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          );
        })}
      </NavigationMenuList>
    </NavigationMenu>
  );
}
